import React, { useState } from 'react';
import { X } from 'lucide-react';
import Draggable from 'react-draggable';

const PRESET_BG_COLORS = ['transparent', '#000000', '#ffffff', '#ff0000', '#00ff00', '#0000ff', '#ffff00'];

const TextInputModal = ({ isOpen, onClose, onSubmit, initialData }) => {
    const [text, setText] = useState('');
    const [fontSize, setFontSize] = useState(40);
    const [color, setColor] = useState('#ffffff');
    const [fontFamily, setFontFamily] = useState('Arial');
    const [backgroundColor, setBackgroundColor] = useState('transparent');
    const [isCustomBg, setIsCustomBg] = useState(false);

    // Load initial data when modal opens
    React.useEffect(() => {
        if (isOpen && initialData) {
            setText(initialData.text || '');
            if (initialData.style) {
                setFontSize(initialData.style.fontSize || 40);
                setColor(initialData.style.color || '#ffffff');
                setFontFamily(initialData.style.fontFamily || 'Arial');
                const initialBg = initialData.style.backgroundColor || 'transparent';
                setBackgroundColor(initialBg);
                setIsCustomBg(!PRESET_BG_COLORS.includes(initialBg));
            }
        } else if (isOpen) {
            // Reset if opening fresh
            setText('');
            setFontSize(40);
            setColor('#ffffff');
            setFontFamily('Arial');
            setBackgroundColor('transparent');
            setIsCustomBg(false);
        }
    }, [isOpen, initialData]);

    const handleSubmit = () => {
        if (text.trim()) {
            onSubmit({
                text: text.trim(),
                style: {
                    fontSize,
                    color,
                    fontFamily,
                    backgroundColor,
                },
            });
            onClose();
        }
    };

    const nodeRef = React.useRef(null);
    const customColorInputRef = React.useRef(null);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-[9999] pointer-events-none flex items-center justify-center">
            <Draggable
                nodeRef={nodeRef}
                handle=".drag-handle"
            >
                <div ref={nodeRef} className="bg-[#FAFAFA] dark:bg-[#111111] rounded-lg shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-white/10 text-gray-900 dark:text-white pointer-events-auto">
                    {/* Header */}
                    <div className="drag-handle cursor-move flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
                        <h2 className="text-xl font-semibold select-none">{initialData ? 'Edit Text Clip' : 'Add Text Clip'}</h2>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors cursor-pointer"
                        >
                            <X className="w-5 h-5" />
                        </button>
                    </div>

                    {/* Content */}
                    <div className="p-6 space-y-4">
                        {/* Text Input */}
                        <div>
                            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400">Text Content</label>
                            <textarea
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                className="w-full bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-4 py-3 focus:outline-none focus:border-primary-500 resize-none text-gray-900 dark:text-white"
                                rows="4"
                                placeholder="Enter your text here..."
                                autoFocus
                            />
                        </div>

                        {/* Styling Options */}
                        <div className="grid grid-cols-2 gap-4">
                            {/* Font Size */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400">Font Size</label>
                                <input
                                    type="number"
                                    value={fontSize}
                                    onChange={(e) => setFontSize(parseInt(e.target.value))}
                                    className="w-full bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 text-gray-900 dark:text-white"
                                    min="10"
                                    max="200"
                                    tabIndex={0}
                                />
                            </div>

                            {/* Text Color */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400">Text Color</label>
                                <div className="flex items-center gap-2">
                                    <input
                                        type="color"
                                        value={color.startsWith('#') && color.length === 7 ? color : '#ffffff'}
                                        onChange={(e) => setColor(e.target.value)}
                                        className="w-12 h-10 rounded cursor-pointer bg-transparent border border-[#E5E7EB] dark:border-gray-700"
                                    />
                                    <input
                                        type="text"
                                        value={color}
                                        onChange={(e) => setColor(e.target.value)}
                                        className="flex-1 bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 text-sm text-gray-900 dark:text-white"
                                    />
                                </div>
                            </div>

                            {/* Font Family */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400">Font</label>
                                <select
                                    value={fontFamily}
                                    onChange={(e) => setFontFamily(e.target.value)}
                                    className="w-full bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 text-gray-900 dark:text-white"
                                >
                                    <option value="Arial">Arial</option>
                                    <option value="Helvetica">Helvetica</option>
                                    <option value="Times New Roman">Times New Roman</option>
                                    <option value="Courier New">Courier New</option>
                                    <option value="Georgia">Georgia</option>
                                    <option value="Verdana">Verdana</option>
                                    <option value="Impact">Impact</option>
                                </select>
                            </div>

                            {/* Background Color */}
                            <div>
                                <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-400">Background</label>
                                <select
                                    value={isCustomBg ? 'custom' : backgroundColor}
                                    onChange={(e) => {
                                        const val = e.target.value;
                                        if (val === 'custom') {
                                            setIsCustomBg(true);
                                            if (backgroundColor === 'transparent' || !backgroundColor.startsWith('#')) {
                                                setBackgroundColor('#000000');
                                            }
                                            setTimeout(() => {
                                                try {
                                                    if (customColorInputRef.current) {
                                                        if (typeof customColorInputRef.current.showPicker === 'function') {
                                                            customColorInputRef.current.showPicker();
                                                        } else {
                                                            customColorInputRef.current.click();
                                                        }
                                                    }
                                                } catch (err) {
                                                    // Fallback if browser blocks showPicker
                                                }
                                            }, 50);
                                        } else {
                                            setIsCustomBg(false);
                                            setBackgroundColor(val);
                                        }
                                    }}
                                    className="w-full bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 text-gray-900 dark:text-white"
                                >
                                    <option value="transparent">Transparent</option>
                                    <option value="#000000">Black</option>
                                    <option value="#ffffff">White</option>
                                    <option value="#ff0000">Red</option>
                                    <option value="#00ff00">Green</option>
                                    <option value="#0000ff">Blue</option>
                                    <option value="#ffff00">Yellow</option>
                                    <option value="custom">Custom...</option>
                                </select>
                                {isCustomBg && (
                                    <div className="flex items-center gap-2 mt-2">
                                        <input
                                            ref={customColorInputRef}
                                            type="color"
                                            value={backgroundColor.startsWith('#') && backgroundColor.length === 7 ? backgroundColor : '#000000'}
                                            onChange={(e) => setBackgroundColor(e.target.value)}
                                            className="w-12 h-10 rounded cursor-pointer bg-transparent border border-[#E5E7EB] dark:border-gray-700"
                                        />
                                        <input
                                            type="text"
                                            value={backgroundColor}
                                            onChange={(e) => setBackgroundColor(e.target.value)}
                                            placeholder="#000000"
                                            className="flex-1 bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg px-3 py-2 focus:outline-none focus:border-primary-500 text-sm text-gray-900 dark:text-white"
                                        />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Preview */}
                        <div className="bg-white dark:bg-gray-800 border border-[#E5E7EB] dark:border-gray-700 rounded-lg p-8 flex items-center justify-center min-h-[120px]">
                            <p
                                style={{
                                    fontSize: `${fontSize}px`,
                                    color: color,
                                    fontFamily: fontFamily,
                                    backgroundColor: backgroundColor !== 'transparent' ? backgroundColor : 'transparent',
                                    padding: backgroundColor !== 'transparent' ? '10px 20px' : '0',
                                }}
                            >
                                {text || 'Preview text...'}
                            </p>
                        </div>
                    </div>

                    {/* Footer */}
                    <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 dark:border-white/10">
                        <button
                            onClick={onClose}
                            className="px-4 py-2 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-900 dark:text-white"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={!text.trim()}
                            className="px-4 py-2 bg-orange-500 hover:bg-orange-600 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-white"
                        >
                            {initialData ? 'Save Changes' : 'Add Text Clip'}
                        </button>
                    </div>
                </div>
            </Draggable>
        </div>
    );
};

export default TextInputModal;
